import { TaskState } from './TaskState';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { WixPatternsContainer } from './WixPatternsContainer';
import { queryMarketListing } from '@wix/bex-utils/@wix/ambassador-devcenter-app-market-listing-v1-market-listing/http';
import {
  MarketListing,
  QueryMarketListingResponse,
} from '@wix/bex-utils/@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';
import { DataExtensionState } from './DataExtensionState';

export interface AppsStateParams {
  container: WixPatternsContainer;
  dataExtension: DataExtensionState;
}

export class AppsState {
  readonly initTask = new TaskState();
  readonly container: WixPatternsContainer;
  readonly dataExtension;

  apps: MarketListing[] = [];

  constructor(params: Readonly<AppsStateParams>) {
    this.container = params.container;
    this.dataExtension = params.dataExtension;

    makeObservable(this, {
      apps: observable.ref,
      appNames: computed,
    });
  }

  init() {
    const { initTask, dataExtension } = this;

    initTask.runOnce(async () => {
      await dataExtension.initTask.status.promise;
      const appIds = (dataExtension.appSchemas || [])
        .map((schema) => schema.appDefId)
        .filter(Boolean) as string[];

      await this._fetchApps(appIds);
    });

    return () => {};
  }

  async _fetchApps(appIds: string[]) {
    if (!appIds.length) {
      return;
    }

    const appRequests = this._getAppsRequests(appIds);

    // Don't care if one of the requests fails, we want to have as many apps as possible
    const responses = await Promise.allSettled(appRequests);
    const allApps = this._aggregateAppsFromResponses(responses);
    runInAction(() => {
      this.apps = allApps;
    });
  }

  get appNames(): Record<string, string> {
    // Prioritize English, allowing other languages to override it.
    return this.apps
      .sort(this._sortByEnglishFirst)
      .reduce(this._extractAppNames, {});
  }

  private _sortByEnglishFirst(a: MarketListing, b: MarketListing): number {
    if (a.languageCode === 'en' && b.languageCode !== 'en') {
      return -1;
    } else if (a.languageCode !== 'en' && b.languageCode === 'en') {
      return 1;
    } else {
      return 0;
    }
  }

  private _extractAppNames(
    acc: Record<string, string>,
    app: MarketListing,
  ): Record<string, string> {
    if (app.appId && app.basicInfo?.name) {
      acc[app.appId] = app.basicInfo.name;
    }
    return acc;
  }

  private _getAppsRequests(appIds: string[]) {
    // Call the API with batches of 50, since the limit is 100, and we get two languages per app (the requested one + english)
    const batchSize = 50;
    const numBatches = Math.ceil(appIds.length / batchSize);

    const batchRequests = [];

    for (let i = 0; i < numBatches; i++) {
      const batchAppIds = appIds.slice(i * batchSize, (i + 1) * batchSize);
      const request = this.container.errorHandler.withErrorHandler(
        () =>
          this.container.httpClient.request(
            queryMarketListing({
              query: {
                filter: {
                  appId: batchAppIds,
                  languageCode: [this.container.environment.language, 'en'],
                  status: 'APPROVED',
                },
              },
            }),
          ),
        {},
      );
      batchRequests.push(request);
    }

    return batchRequests;
  }

  private _aggregateAppsFromResponses(
    responses: PromiseSettledResult<{
      data: QueryMarketListingResponse;
      status: number;
      statusText: string;
      headers: Record<string, string | undefined>;
    }>[],
  ) {
    const allApps: MarketListing[] = [];

    responses.forEach((response) => {
      if (response.status === 'fulfilled') {
        const {
          data: { marketListing: apps },
        } = response.value;
        if (apps) {
          allApps.push(...apps);
        }
      }
    });

    return allApps;
  }
}
